import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { APP_CONFIG } from '@lb/config/interfaces/iapp-settings.interface';
import { IRouteGuardSettings } from './interface/iroute-guard-settings.interface';
import { AuthenticationService } from '../services/authentication.service';
import { of } from 'rxjs';

export const AccessDeniedRedirect: string = '/403';

export const ConfiguredRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
	const authService: AuthenticationService = inject(AuthenticationService);
	const router = inject(Router);
	const appConfig = inject(APP_CONFIG);

	const routeConfig: IRouteGuardSettings = {
		entitlements: [],
		redirect: AccessDeniedRedirect,
		...route.data['routeGuardSettings']
	};

	// Feature toggle first: a page that is not enabled in this environment is not there to be
	// authorized, so entitlements are never consulted for it. Absent flag reads as off (fail closed).
	if (routeConfig.featureToggle && !(appConfig.features?.[routeConfig.featureToggle] ?? false)) {
		redirectTo(router, routeConfig.featureToggleRedirect ?? routeConfig.redirect);
		return of(false);
	}

	let entitlementAccess: boolean = routeConfig.entitlements.length === 0;

	if (routeConfig.entitlements.length > 0) {
		if (authService.hasAnyEntitlement(routeConfig.entitlements)) {
			entitlementAccess = true;
		}
	}

	if (!entitlementAccess) {
		// Check if user has alternative entitlements and redirect accordingly
		if (routeConfig.alternativeEntitlements && routeConfig.alternativeEntitlements.length > 0) {
			if (authService.hasAnyEntitlement(routeConfig.alternativeEntitlements)) {
				// Use the alternativeRedirect that should now be properly configured in route data
				const alternativeRedirect = routeConfig.alternativeRedirect;

				if (alternativeRedirect) {
					if (alternativeRedirect.startsWith('http')) {
						// External URL redirect
						window.location.href = alternativeRedirect;
						return of(false);
					} else {
						// Internal route redirect
						router.navigate([alternativeRedirect]);
						return of(false);
					}
				} else {
					// If no alternativeRedirect is configured, this is a configuration error
					console.warn('Alternative entitlements configured but no alternativeRedirect provided in route data');
				}
			}
		}

		// Default redirect behavior
		redirectTo(router, routeConfig.redirect);
	}

	return of(entitlementAccess);
};

/** A static page (`.html`) leaves the app; anything else is an in-app route. */
function redirectTo(router: Router, target: string): void {
	if (target.includes('.html')) {
		window.location.href = target;
	} else {
		router.navigate([target]);
	}
}

export const canActivateChild: CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => ConfiguredRouteGuard(route, state);
